import React, { useEffect, useMemo, useState } from 'react';
import './Ting.css';

// Components
import EditTing from '../../../../components/Ting/EditTing';
import Media from '../../../../components/Ting/Media';

// MUI Components
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import { doc, getDoc } from 'firebase/firestore';

import { db } from '../../../../firebase';
import { getTing } from '../../../../utils/firebase/getTing';
import { handleTingUpdate } from '../../../../utils/firebase/updateTing';
import useTingStore from '../../../../stores/tingStore';
import { getCollectionCount } from '../../../../utils/getCollectionCount';

export default function Ting() {
    const tingDoc = useTingStore(state => state.tingDoc);
    const setTingDoc = useTingStore(set => set.setTingDoc);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [deletingCommentId, setDeletingCommentId] = useState(null);
    const [commentUsernames, setCommentUsernames] = useState({});
    const tingId = window.location.pathname.split('/')[2];

    const showFeedback = (severity, message) => {
        setFeedback({ severity, message });
    };

    const handleCloseFeedback = (_, reason) => {
        if (reason === 'clickaway') return;
        setFeedback(null);
    };

    useEffect(() => {
        const assignTing = async () => {
            const tingDocData = await getTing(tingId);
            setTingDoc(tingDocData.tingDocDataReceived);
            setLoading(tingDocData.loaded);
        };

        assignTing();
    }, [tingId, setTingDoc]);

    const handleDelete = async () => {
        const confirmDelete = window.confirm('Are you sure you want to delete this Ting? This action cannot be undone.');
        if (!confirmDelete) return;

        setSaving(true);
        setDeleting(true);
        setSaveSuccess(false);
        setFeedback(null);
        const deletedDoc = {
            ...tingDoc,
            deletedTime: new Date(),
        };

        try {
            const deleted = await handleTingUpdate(deletedDoc, tingId, { showAlert: false });

            if (deleted) {
                setTingDoc(deletedDoc);
                showFeedback('success', 'Ting deleted successfully. The change has been saved.');
            } else {
                showFeedback('error', 'Could not delete this Ting. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting Ting:', error);
            showFeedback('error', 'Could not delete this Ting. Please try again.');
        } finally {
            setSaving(false);
            setDeleting(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveSuccess(false);
        setFeedback(null);
        try {
            const saved = await handleTingUpdate(tingDoc, tingId, { showAlert: false });

            if (saved) {
                setSaveSuccess(true);
                showFeedback('success', 'Changes saved successfully.');
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                showFeedback('error', 'Could not save changes. Please try again.');
            }
        } catch (error) {
            console.error('Error saving:', error);
            showFeedback('error', 'Could not save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const getDateFromTimestamp = (value) => {
        let date;
        if (value?.seconds) {
            date = new Date(value.seconds * 1000);
        } else if (typeof value === 'number') {
            date = new Date(value < 1e12 ? value * 1000 : value);
        } else if (typeof value === 'string') {
            date = new Date(value);
        } else if (value instanceof Date) {
            date = value;
        }

        if (!date || Number.isNaN(date.getTime())) return null;
        return date;
    };

    const formatTimestamp = (value, options = {}) => {
        if (!value) return '';

        const { relativeDay = false } = options;
        const date = getDateFromTimestamp(value);
        if (!date) return '';

        if (!relativeDay) {
            return date.toLocaleString();
        }

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayDifference = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24));
        const timeText = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

        if (dayDifference === 0) {
            return `Today, ${timeText}`;
        }

        if (dayDifference === 1) {
            return `Yesterday, ${timeText}`;
        }

        return date.toLocaleString();
    };

    const getCommentText = (comment) => {
        if (typeof comment === 'string') return comment;
        if (comment?.comment) return comment.comment;
        if (comment?.text) return comment.text;
        if (comment?.message) return comment.message;
        if (comment?.body) return comment.body;
        return JSON.stringify(comment);
    };

    const getCommentUserId = (comment) => {
        if (!comment || typeof comment !== 'object') return '';

        const idFromComment = comment?.userId || comment?.uid || comment?.authorId || comment?.user?.uid || comment?.user?.id;
        return typeof idFromComment === 'string' ? idFromComment : '';
    };

    const getCommentAuthor = (comment, commentUserId) => {
        if (commentUserId && commentUsernames[commentUserId]) {
            return commentUsernames[commentUserId];
        }

        if (!comment || typeof comment !== 'object') {
            return commentUserId || 'Unknown user';
        }

        const inlineAuthorName =
            comment?.username ||
            comment?.userName ||
            comment?.name ||
            (typeof comment?.user === 'string' ? comment.user : '') ||
            comment?.author ||
            comment?.userData?.username;

        if (typeof inlineAuthorName === 'string' && inlineAuthorName.trim()) {
            return inlineAuthorName;
        }

        return commentUserId || 'Unknown user';
    };

    const getCommentTime = (comment) => {
        if (!comment || typeof comment !== 'object') return '';
        return formatTimestamp(
            comment?.createdAt ||
            comment?.createdTime ||
            comment?.timestamp ||
            comment?.time ||
            comment?.date
        );
    };

    const commentEntries = useMemo(() => {
        const rawComments = tingDoc?.comments;

        if (Array.isArray(rawComments)) {
            return rawComments.map((comment, index) => ({
                id: `array-${index}`,
                comment,
                arrayIndex: index,
                commentUserId: getCommentUserId(comment),
            }));
        }

        if (rawComments && typeof rawComments === 'object') {
            return Object.entries(rawComments)
                .filter(([key, value]) => !((key === 'count' || key === 'total') && typeof value === 'number'))
                .map(([key, comment]) => ({
                id: `object-${key}`,
                comment,
                objectKey: key,
                commentUserId: getCommentUserId(comment),
            }));
        }

        return [];
    }, [tingDoc?.comments]);

    const missingCommentUserIds = useMemo(() => {
        const uniqueCommentUserIds = [...new Set(commentEntries.map((entry) => entry.commentUserId).filter(Boolean))];
        return uniqueCommentUserIds.filter((userId) => !(userId in commentUsernames));
    }, [commentEntries, commentUsernames]);

    useEffect(() => {
        if (missingCommentUserIds.length === 0) {
            return;
        }

        let cancelled = false;

        const loadCommentUsernames = async () => {
            const fetchedUsernames = await Promise.all(
                missingCommentUserIds.map(async (userId) => {
                    try {
                        const userSnapshot = await getDoc(doc(db, 'users', userId));

                        if (!userSnapshot.exists()) {
                            return [userId, null];
                        }

                        const userData = userSnapshot.data();
                        const fullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();
                        const resolvedName = userData?.username || fullName || null;

                        return [userId, resolvedName];
                    } catch (error) {
                        console.error('Error fetching comment user:', userId, error);
                        return [userId, null];
                    }
                })
            );

            if (cancelled) {
                return;
            }

            setCommentUsernames((previousMap) => {
                const nextMap = { ...previousMap };
                fetchedUsernames.forEach(([userId, username]) => {
                    nextMap[userId] = username;
                });
                return nextMap;
            });
        };

        loadCommentUsernames();

        return () => {
            cancelled = true;
        };
    }, [missingCommentUserIds]);

    const likesCount = getCollectionCount(tingDoc?.likes);
    const commentsCount = getCollectionCount(tingDoc?.comments);

    const handleDeleteComment = async (commentEntry) => {
        const existingComments = tingDoc?.comments;
        const isArrayComments = Array.isArray(existingComments);
        const isObjectComments = existingComments && typeof existingComments === 'object';

        if (!isArrayComments && !isObjectComments) return;

        const confirmDelete = window.confirm('Delete this comment?');
        if (!confirmDelete) return;

        let updatedComments;

        if (isArrayComments) {
            updatedComments = existingComments.filter((_, index) => index !== commentEntry.arrayIndex);
        } else {
            updatedComments = { ...existingComments };
            delete updatedComments[commentEntry.objectKey];
        }

        const previousDoc = tingDoc;
        const updatedDoc = { ...tingDoc, comments: updatedComments };

        setDeletingCommentId(commentEntry.id);
        setTingDoc(updatedDoc);

        try {
            const updated = await handleTingUpdate(updatedDoc, tingId, { showAlert: false });
            if (updated) {
                setSaveSuccess(true);
                showFeedback('success', 'Comment deleted successfully.');
                setTimeout(() => setSaveSuccess(false), 3000);
            } else {
                setTingDoc(previousDoc);
                showFeedback('error', 'Could not delete this comment. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
            setTingDoc(previousDoc);
            showFeedback('error', 'Could not delete this comment. Please try again.');
        } finally {
            setDeletingCommentId(null);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
            <div className="ting-page">
                <Paper elevation={0} className="ting-page__hero">
                    <div>
                        <p className="ting-page__eyebrow">Ting Editor</p>
                        <h1 className="ting-page__title">
                            {tingDoc?.animal ? `${tingDoc.animal} Sighting` : 'Edit Ting'}
                        </h1>
                        <div className="ting-page__meta">
                            <span>ID: {tingId}</span>
                            {tingDoc?.userId && <span>Tinger: {tingDoc.userId}</span>}
                            {tingDoc?.postTime && <span>Posted: {formatTimestamp(tingDoc.postTime, { relativeDay: true })}</span>}
                        </div>
                    </div>

                </Paper>

                {saveSuccess && (
                    <Alert severity="success">
                        Changes saved successfully.
                    </Alert>
                )}

                {!saveSuccess && tingDoc?.deletedTime && (
                    <Alert severity="warning">
                        This Ting has been marked as deleted.
                    </Alert>
                )}

                {loading ? (
                    <Paper elevation={0} className="ting-page__loading">
                        <CircularProgress size={24} />
                        <Typography variant="body1">Loading ting details...</Typography>
                    </Paper>
                ) : (
                    <>
                        <div className="ting-page__stats">
                            <Paper elevation={0} className="ting-stat-card">
                                <span className="ting-stat-card__label">Comments</span>
                                <strong>{commentsCount.toLocaleString()}</strong>
                            </Paper>
                            <Paper elevation={0} className="ting-stat-card">
                                <span className="ting-stat-card__label">Likes</span>
                                <strong>{likesCount.toLocaleString()}</strong>
                            </Paper>
                            <Paper elevation={0} className="ting-stat-card">
                                <span className="ting-stat-card__label">Content Type</span>
                                <strong>{tingDoc?.contentType || '-'}</strong>
                            </Paper>
                            <Paper elevation={0} className="ting-stat-card">
                                <span className="ting-stat-card__label">Park</span>
                                <strong>{tingDoc?.park || '-'}</strong>
                            </Paper>
                        </div>

                        <div className="ting-page__content-grid">
                            <div className="ting-page__main">
                                <Paper elevation={0} className="ting-panel">
                                    <div className="ting-panel__header">
                                        <Typography variant="h6">Media</Typography>
                                        <Typography className="ting-panel__subtext" variant="body2">
                                            Review or replace the current media attached to this ting.
                                        </Typography>
                                    </div>
                                    <Media />
                                </Paper>

                                <Paper elevation={0} className="ting-panel ting-panel--editor">
                                    <div className="ting-panel__header">
                                        <Typography variant="h6">Ting Details</Typography>
                                        <Typography className="ting-panel__subtext" variant="body2">
                                            Update sighting data, map coordinates and social ownership in one workflow.
                                        </Typography>
                                    </div>
                                    <EditTing />
                                </Paper>
                            </div>

                            <aside className="ting-page__sidebar">
                                <Paper elevation={0} className="ting-panel ting-panel--sticky">
                                    <div className="ting-panel__header">
                                        <Typography variant="h6">Comments ({commentsCount})</Typography>
                                        <Typography className="ting-panel__subtext" variant="body2">
                                            Remove inappropriate or incorrect comments.
                                        </Typography>
                                    </div>

                                    {commentEntries.length === 0 ? (
                                        <Typography variant="body2" className="ting-comments__empty">
                                            No comments on this ting.
                                        </Typography>
                                    ) : (
                                        <Box className="ting-comments__list">
                                            {commentEntries.map((entry) => (
                                                <div className="ting-comment" key={entry.id}>
                                                    <div className="ting-comment__header">
                                                        <div className="ting-comment__author">
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {getCommentAuthor(entry.comment, entry.commentUserId)}
                                                            </Typography>
                                                            {getCommentTime(entry.comment) && (
                                                                <Typography variant="caption" sx={{ color: 'var(--color-muted)' }}>
                                                                    {getCommentTime(entry.comment)}
                                                                </Typography>
                                                            )}
                                                        </div>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleDeleteComment(entry)}
                                                            disabled={deletingCommentId === entry.id}
                                                        >
                                                            {deletingCommentId === entry.id ? 'Deleting...' : 'Delete'}
                                                        </Button>
                                                    </div>
                                                    <Typography variant="body2" sx={{ mt: 0.75, wordBreak: 'break-word' }}>
                                                        {getCommentText(entry.comment)}
                                                    </Typography>
                                                </div>
                                            ))}
                                        </Box>
                                    )}
                                </Paper>

                                <Paper elevation={0} className="ting-panel">
                                    <div className="ting-panel__header">
                                        <Typography variant="h6">Quick Info</Typography>
                                    </div>
                                    <dl className="ting-quick-info">
                                        <div>
                                            <dt>Ting Time</dt>
                                            <dd>{formatTimestamp(tingDoc?.tingTime, { relativeDay: true }) || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt>Post Time</dt>
                                            <dd>{formatTimestamp(tingDoc?.postTime, { relativeDay: true }) || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt>Road</dt>
                                            <dd>{tingDoc?.roadname || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt>Location</dt>
                                            <dd>{tingDoc?.locationDescription || '-'}</dd>
                                        </div>
                                        <div>
                                            <dt>Visibility</dt>
                                            <dd>{tingDoc?.visibility ?? '-'}</dd>
                                        </div>
                                        <div>
                                            <dt>Traffic</dt>
                                            <dd>{tingDoc?.traffic ?? '-'}</dd>
                                        </div>
                                    </dl>
                                </Paper>

                                <div className="ting-page__actions">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSave}
                                        disabled={saving}
                                        startIcon={saving && !deleting ? <CircularProgress size={18} color="inherit" /> : null}
                                    >
                                        {saving && !deleting ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleDelete}
                                        disabled={saving || Boolean(tingDoc?.deletedTime)}
                                        startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : null}
                                    >
                                        {deleting ? 'Deleting...' : tingDoc?.deletedTime ? 'Ting Deleted' : 'Delete Ting'}
                                    </Button>
                                </div>
                            </aside>
                        </div>
                    </>
                )}
            </div>
            <Snackbar
                open={Boolean(feedback)}
                autoHideDuration={4000}
                onClose={handleCloseFeedback}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {feedback ? (
                    <Alert
                        onClose={handleCloseFeedback}
                        severity={feedback.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {feedback.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Container>
    );
}
